import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { IFeatureAttributeDto } from "App/Typings/AbacFeatureAttribute";
import { useFormik } from "formik";
import { useSyncFeatureClientAttributes } from "Pages/ABAC/AbacAppQueries";
import { AnySchema, array, boolean, object } from "yup";

interface IAbacClientOverrideFormProps {
  selectedDistributionType: "Default" | "Custom";
  handleChangeModalDistributionType: (type: "Default" | "Custom") => void;
  defaultDistribution: IFeatureAttributeDto[];
  draftVariations: IFeatureAttributeDto[];
  handleUpdateDraftVariations: (variations: IFeatureAttributeDto[]) => void;
  onSubmit: (values: IFeatureAttributeDto[]) => void;
}

const validationSchema = object().shape({
  useDefault: boolean(),
  distribution: array<AnySchema, IFeatureAttributeDto>().when("useDefault", {
    is: false,
    then: (schema) =>
      schema
        .test(
          "one-or-more-variations",
          "At least one variation is required",
          (variations) => variations && variations.length > 0
        )
        .test(
          "values-in-range",
          "Variation distribution values must be numbers between 0 and 100",
          (variations) =>
            variations?.every((variation) => variation.distribution >= 0 && variation.distribution <= 100)
        )
        .test(
          "100-percent-total",
          "Sum of distribution values must be 100%",
          (variations) => variations?.reduce((acc: number, attr) => acc + attr.distribution, 0) === 100
        ),
    otherwise: (schema) => schema.optional(),
  }),
});

export function AbacClientOverrideForm({
  selectedDistributionType,
  handleChangeModalDistributionType,
  defaultDistribution,
  draftVariations,
  handleUpdateDraftVariations,
  onSubmit,
}: IAbacClientOverrideFormProps) {
  const { isLoading } = useSyncFeatureClientAttributes();
  const hasCustomDistribution = selectedDistributionType === "Custom";

  const formik = useFormik({
    initialValues: {
      useDefault: !hasCustomDistribution,
      distribution: draftVariations,
    },
    validationSchema,
    onSubmit: (values) => {
      onSubmit(values.useDefault ? [] : values.distribution);
    },
  });

  const editVariation = (updatedVariation: IFeatureAttributeDto) => {
    const updatedVariations = [...formik.values.distribution];
    const updatedVariationIndex = updatedVariations.findIndex(
      (v) => v.attributeName === updatedVariation.attributeName
    );
    updatedVariations[updatedVariationIndex] = updatedVariation;
    formik.setFieldValue("distribution", updatedVariations);
    handleUpdateDraftVariations(updatedVariations);
  };

  const toggleCustomDistribution = (checked: boolean) => {
    formik.setFieldValue("useDefault", checked);
    handleChangeModalDistributionType(checked ? "Default" : "Custom");
  };

  const currentSum = draftVariations.reduce((sum, attr) => sum + (attr.distribution || 0), 0);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box>
        <Typography variant="subtitle1">Default Variation Settings</Typography>
        <TableContainer sx={{ maxHeight: 300, mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Distribution</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {defaultDistribution.map((variation) => (
                <TableRow key={variation.id}>
                  <TableCell>{variation.attributeName}</TableCell>
                  <TableCell>{variation.distribution}%</TableCell>
                  <TableCell>
                    <Box sx={{ wordWrap: "break-word", maxWidth: "50ch" }}>
                      {variation.attributeDescription}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="subtitle1">Client Override Settings</Typography>
        <Typography variant="body2" fontStyle="italic" sx={{ my: 1 }}>
          Override the default settings to customize the distribution for this client.
        </Typography>
        <Collapse in={selectedDistributionType === "Custom"} timeout={300}>
          <Box>
            <Box
              sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr", lg: "1fr 1fr 1fr" } }}
              gap={2}
              alignItems="center"
              mb={2}
            >
              {formik.values.distribution.map((variation) => (
                <TextField
                  key={variation.id}
                  name={`distribution.${variation.id}`}
                  label={`${variation.attributeName} Rate (%)`}
                  type="number"
                  fullWidth
                  InputProps={{ inputProps: { min: 0, max: 100 } }}
                  value={variation.distribution}
                  onChange={(e) => {
                    !formik.values.useDefault &&
                      editVariation({ ...variation, distribution: Number(e.target.value) });
                  }}
                  disabled={formik.values.useDefault}
                  margin="normal"
                  size="small"
                  sx={{ flexGrow: { xs: 1, md: 1 }, width: { xs: "100%", md: "auto" } }}
                  helperText={formik.touched.distribution && formik.errors.distribution}
                />
              ))}
            </Box>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="body1" fontWeight="medium">
                  Total Distribution:
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  color={currentSum !== 100 ? "error" : "success"}
                >
                  {currentSum}%
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={Math.min(currentSum, 100)}
                color={currentSum !== 100 ? "error" : "success"}
                sx={{
                  height: 10,
                  borderRadius: 1,
                  "& .MuiLinearProgress-bar": { transitionDuration: ".2s" },
                }}
              />

              <Collapse in={!!formik.errors.distribution} timeout={300}>
                <Typography fontSize="small" fontStyle="italic" color="error" sx={{ mt: 1 }}>
                  {formik.errors.distribution}
                </Typography>
              </Collapse>
            </Box>
          </Box>
        </Collapse>
        <FormControlLabel
          label="Use Default Distribution?"
          sx={{ mt: 2 }}
          control={
            <Checkbox
              name="useDefault"
              checked={formik.values.useDefault}
              onChange={(e) => toggleCustomDistribution(e.target.checked)}
            />
          }
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          type="submit"
          variant="contained"
          sx={{ marginTop: 2, marginBottom: 2 }}
          disabled={formik.isSubmitting || formik.isValidating || !formik.isValid || isLoading}
        >
          Save Changes
        </Button>
      </Box>
    </form>
  );
}
